/**
 * "Used by Yacht" -- how often boats of each size have anchored here.
 *
 * The point of it is reassurance a captain cannot get from a chart: a 78 m
 * yacht wants to know that boats her size have lain here and left, not that
 * the pilot book calls the bay sheltered.
 *
 * It is also the only thing in this app that tells one captain anything about
 * another's movements, so what makes it safe is its shape rather than a
 * permission check. Counts, banded four to twenty metres wide, no names, no
 * dates -- and the server counts **completed** anchorings only, so this can
 * never answer "is there a 90 m yacht in this bay right now". That question
 * belongs to the visibility rule, which answers it only with consent.
 *
 * Bands are ABG's own description of the fleet. A band with no anchorings is
 * still shown: "LOA 90m+: 0 time(-s)" is information, and hiding the empty
 * rows would leave a captain unsure whether the band was empty or omitted.
 */

import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { AnchorageUsage } from '../lib/types'

function bandLabel(min: number, max: number | null): string {
  // Bands are half-open server-side (a 45.5 m yacht is in the 24-45 band), but
  // ABG label them by whole metres, so the label reads 24-45 rather than
  // 24-46. Keep the two in step: the label is derived, not typed twice.
  return max === null ? `LOA ${min}m+` : `LOA ${min}-${max - 1}m`
}

export function UsageSummary({ anchorageId }: { anchorageId: string }) {
  const [usage, setUsage] = useState<AnchorageUsage | null>(null)

  useEffect(() => {
    let cancelled = false
    setUsage(null)
    api
      .get<AnchorageUsage>(`/api/anchorages/${anchorageId}/usage`)
      .then((data) => !cancelled && setUsage(data))
      // Silent: this is context, not something to apply against. A captain on a
      // bad connection should still get the anchorage and the Apply button.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [anchorageId])

  if (!usage) return null

  return (
    <div className="stack-sm">
      <div className="eyebrow">Used by Yacht</div>
      <ul className="usage-list">
        {usage.bands.map((band) => (
          <li key={band.min_m}>
            <span>{bandLabel(band.min_m, band.max_m)}:</span>
            <span className="mono">{band.count} time(-s)</span>
          </li>
        ))}
        {usage.unknown_loa > 0 && (
          <li>
            {/* Reported rather than dropped -- a summary that quietly omitted
                rows would understate a place. */}
            <span>Length not on file:</span>
            <span className="mono">{usage.unknown_loa} time(-s)</span>
          </li>
        )}
        {usage.below_bands > 0 && (
          <li>
            <span>{`Under ${usage.bands[0]?.min_m ?? 24}m:`}</span>
            <span className="mono">{usage.below_bands} time(-s)</span>
          </li>
        )}
      </ul>
      {usage.total === 0 && (
        <p className="caption">
          Nothing recorded here yet. That is not a warning — most of the registry
          has never been reported against.
        </p>
      )}
    </div>
  )
}
